import {axiCrypto, axiPayment, GetListOutput, GetListParams} from '@shared/api';
import {validateData} from '@shared/utils';
import {Currency, CurrencyEventSchedule, EventTemplate} from '../types';
import {
  v8nSchemaOfGetCurrenciesResponse,
  v8nSchemaOfGetCurrencyEventSchedulesResponse,
  v8nSchemaOfGetEventTemplatesResponse,
  v8nSchemaOfGetPaymentCurrenciesResponse,
} from './backendSchemas';
import {
  GetCurrenciesResponse,
  GetCurrencyEventSchedulesResponse,
  GetEventTemplatesResponse,
  GetPaymentCurrenciesResponse,
} from './backendTypes';
import {
  mapCurrencyEventScheduleFromBackend,
  mapCurrencyFromBackend,
  mapEventTemplateFromBackend,
} from './dataMappers';

export interface GetCurrencyEventSchedulesParams extends GetListParams {
  currencyId?: string;
  templateId?: string;
}

export interface CreateCurrencyEventScheduleParams {
  currencyId: string;
  templateId: string;
  cron: string;
  runImmediately?: boolean;
}

export const getCurrencies = async (): Promise<Currency[]> => {
  const response =
    await axiCrypto.get<GetCurrenciesResponse>('/admin/currencies');

  try {
    validateData(
      response.data,
      v8nSchemaOfGetCurrenciesResponse,
      'getCurrencies',
    );
  } catch {
    //
  }

  return (response.data ?? []).map(item => mapCurrencyFromBackend(item));
};

export const getPaymentCurrencies = async (): Promise<string[]> => {
  const {data} = await axiPayment.get<GetPaymentCurrenciesResponse>(
    '/admin/currencies',
    {params: {enabled: true}},
  );
  validateData(
    data,
    v8nSchemaOfGetPaymentCurrenciesResponse,
    'getPaymentCurrencies',
  );
  return data.array;
};

export const getEventTemplates = async (): Promise<EventTemplate[]> => {
  const response = await axiCrypto.get<GetEventTemplatesResponse>(
    '/admin/events/templates',
  );

  try {
    validateData(
      response.data,
      v8nSchemaOfGetEventTemplatesResponse,
      'getEventTemplates',
    );
  } catch {
    //
  }

  return (response.data ?? []).map(item => mapEventTemplateFromBackend(item));
};

export const getCurrencyEventSchedules = async ({
  currencyId,
  templateId,
  page,
  pageSize,
}: GetCurrencyEventSchedulesParams): Promise<
  GetListOutput<CurrencyEventSchedule>
> => {
  const response = await axiCrypto.get<GetCurrencyEventSchedulesResponse>(
    '/admin/currencies/events/schedules',
    {
      params: {
        currency_id: currencyId,
        template_id: templateId,
        page,
        per_page: pageSize,
      },
    },
  );

  try {
    validateData(
      response.data,
      v8nSchemaOfGetCurrencyEventSchedulesResponse,
      'getCurrencyEventSchedules',
    );
  } catch {
    //
  }

  const {
    array,
    total,
    total_pages,
    page: pageFromBackend,
    per_page,
  } = response.data;
  const items =
    array?.map(item => mapCurrencyEventScheduleFromBackend(item)) ?? [];

  return {
    items,
    page: pageFromBackend,
    pageSize: per_page,
    total,
    totalPages: total_pages,
  };
};

export const createCurrencyEventSchedule = async ({
  currencyId,
  templateId,
  cron,
  runImmediately,
}: CreateCurrencyEventScheduleParams): Promise<void> => {
  await axiCrypto.post('/admin/currencies/events/schedules', {
    currency_id: currencyId,
    template_id: templateId,
    cron,
    run_immediately: runImmediately,
  });
};

export const deleteCurrencyEventSchedule = async (
  scheduleId: string,
): Promise<void> => {
  await axiCrypto.delete(`/admin/currencies/events/schedules/${scheduleId}`);
};
